/**
 * O tamanho do texto — Normal (100%) e Grande (112,5%).
 *
 * Ele mexe no `font-size` da **raiz**, e só nele: como toda medida de texto da
 * folha está em `rem` (Fase 1A), um degrau na raiz leva a escala inteira junto,
 * incluindo espaçamentos e alturas que derivam dela. É por isso que a regra "nada
 * de `px` em texto" e este ajuste são a mesma decisão.
 *
 * **Antes da primeira pintura, e sem ida ao servidor.** A preferência é lida do
 * `localStorage` e aplicada no topo de `main.tsx`, antes de `createRoot`. Uma
 * consulta ao servidor não cabe aqui por definição: ela chegaria depois do
 * primeiro quadro, e o que o usuário veria é a tela pular de tamanho — que é
 * exatamente o defeito que "sem piscar" proíbe.
 *
 * **Por conta, e não por navegador.** A chave leva o identificador da conta, e
 * há uma segunda cópia sem conta — a última aplicada — porque no instante do
 * boot ainda não se sabe quem entrou. `reconciliar` roda quando a sessão chega:
 * se a conta tinha outra preferência, ela vence e a tela troca uma vez só.
 */

export type TamanhoDoTexto = "normal" | "grande";

const CHAVE_ULTIMO = "noesis:tamanho-do-texto";
const chaveDaConta = (usuarioId: string) => `${CHAVE_ULTIMO}:${usuarioId}`;

function valido(v: string | null): TamanhoDoTexto | null {
  return v === "normal" || v === "grande" ? v : null;
}

function ler(chave: string): TamanhoDoTexto | null {
  try {
    return valido(localStorage.getItem(chave));
  } catch {
    // Navegador com armazenamento bloqueado ainda precisa exibir o app.
    return null;
  }
}

function gravar(chave: string, valor: TamanhoDoTexto): void {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    /* segue sem guardar */
  }
}

/** Escreve o atributo que a folha lê. `normal` não põe atributo nenhum. */
export function aplicarTamanho(tamanho: TamanhoDoTexto): void {
  const raiz = document.documentElement;
  if (tamanho === "grande") raiz.setAttribute("data-texto", "grande");
  else raiz.removeAttribute("data-texto");
}

/** O que aplicar no boot, antes de saber quem entrou. */
export function tamanhoGuardado(): TamanhoDoTexto {
  return ler(CHAVE_ULTIMO) ?? "normal";
}

/** O da conta, quando ela já tem um; senão, o último aplicado neste navegador. */
export function tamanhoDaConta(usuarioId: string): TamanhoDoTexto {
  return ler(chaveDaConta(usuarioId)) ?? tamanhoGuardado();
}

/** Guarda nas duas chaves e aplica. */
export function definirTamanho(usuarioId: string | null, tamanho: TamanhoDoTexto): void {
  gravar(CHAVE_ULTIMO, tamanho);
  if (usuarioId) gravar(chaveDaConta(usuarioId), tamanho);
  aplicarTamanho(tamanho);
}

/** Chamado quando a sessão chega: a preferência da conta vence a do boot. */
export function reconciliarComAConta(usuarioId: string): TamanhoDoTexto {
  const daConta = tamanhoDaConta(usuarioId);
  aplicarTamanho(daConta);
  gravar(CHAVE_ULTIMO, daConta);
  return daConta;
}
